/**
 * Fait : le SECOND chemin d'envoi, borné au dépôt d'un document — celui qui
 *   dit OÙ EN EST l'envoi pendant qu'il se fait. Il rend exactement le même
 *   type de résultat que le chemin ordinaire, et lui emprunte sa traduction :
 *   un refus reste un refus, une non-réponse reste une non-réponse.
 *
 *   POURQUOI IL EXISTE, écrit le 2026-08-24. `fetch` ne rend rien tant que
 *   l'envoi n'est pas fini : dix mégaoctets sur une liaison familiale, c'est
 *   plusieurs dizaines de secondes d'attente MUETTE, indiscernables d'un
 *   blocage. C'est précisément le défaut que le module d'appel avait été écrit
 *   pour fermer, et le refaire au lot le plus lourd serait une régression
 *   connue d'avance. `XMLHttpRequest` sait, lui, annoncer sa progression : ce
 *   n'est pas une bibliothèque de plus, c'est le navigateur qui la donne.
 *
 * Ne fait pas : il ne décide d'aucun palier — l'écran choisit ce qu'il annonce
 *   et quand, parce qu'une région vive rafraîchie soixante fois par seconde
 *   rend un lecteur d'écran inutilisable — n'affiche rien, et ne connaît aucune
 *   route : le chemin lui est donné.
 */

import { sansReponse, traduitUneReponse, type Issue } from './appel.ts';

/**
 * LE SILENCE MAXIMAL TOLÉRÉ, en millisecondes.
 *
 * Ce n'est pas une durée d'envoi : c'est une durée SANS LA MOINDRE AVANCÉE. La
 * veille est réarmée à chaque octet parti et à chaque miette de réponse, donc
 * un envoi de dix mégaoctets sur une liaison familiale ne l'atteint jamais —
 * seule l'atteint une liaison à demi ouverte, celle qui a pris les octets et
 * ne répondra plus rien.
 *
 * POURQUOI ELLE EXISTE, mesuré le 2026-08-25 par une épreuve indépendante :
 * `XMLHttpRequest` n'a AUCUNE borne de temps par défaut. Quinze secondes après
 * un envoi vers un serveur muet, le bouton était encore désactivé sur « Envoi
 * en cours… », la région vive en était restée à « Envoi commencé. », et rien
 * ne permettait d'abandonner. Pour quelqu'un qui ne voit pas son écran, c'est
 * une attente sans fin et sans un mot.
 */
const SILENCE_MAXIMAL_MS = 12_000;

/** Ce qu'un envoi avec progression demande. */
export interface DemandeDEnvoi {
  /** La méthode HTTP, écrite en toutes lettres par l'appelant. */
  readonly methode: string;

  /** Les octets à envoyer — un `File` choisi, tel quel, jamais recopié. */
  readonly octets: Blob;

  /**
   * Appelé à chaque avancée, avec une part entre 0 et 1. Il peut être appelé
   * très souvent : c'est à l'écran de n'en retenir que ce qu'il annonce.
   */
  surProgression: (part: number) => void;
}

/**
 * Envoie des octets en corps BRUT et rend l'issue traduite.
 *
 * Trois façons de finir, et une seule est une réponse : `load` (le serveur a
 * répondu, quel que soit le code), `error`/`timeout`/`abort` (il n'a rien dit).
 * Une promesse ne se résout qu'une fois : le premier arrivé tranche.
 */
export function envoieAvecProgression<T>(
  chemin: string,
  demande: DemandeDEnvoi,
): Promise<Issue<T>> {
  return new Promise((resoud) => {
    const requete = new XMLHttpRequest();

    // La veille : elle coupe l'attente si PLUS RIEN n'avance, et elle est
    // désarmée dès qu'une issue est rendue — une promesse ne se résout qu'une
    // fois, la minuterie ne doit pas survivre à ce qu'elle surveillait.
    let veille = 0;

    const rearme = () => {
      window.clearTimeout(veille);
      veille = window.setTimeout(() => requete.abort(), SILENCE_MAXIMAL_MS);
    };

    const conclus = (issue: Issue<T>) => {
      window.clearTimeout(veille);
      resoud(issue);
    };

    requete.upload.addEventListener('progress', (evenement) => {
      rearme();

      // Sans longueur connue, une part n'a pas de sens : on se tait plutôt
      // que d'inventer une avancée.
      if (evenement.lengthComputable && evenement.total > 0) {
        demande.surProgression(evenement.loaded / evenement.total);
      }
    });

    // Les octets sont partis : c'est au serveur de parler, et la veille
    // continue de compter — c'est même là qu'elle sert.
    requete.upload.addEventListener('load', rearme);
    requete.addEventListener('progress', rearme);

    requete.addEventListener('load', () => {
      // Le corps peut être vide (204) ou illisible (une passerelle qui parle
      // à la place de l'atelier) : dans les deux cas `null`, et c'est la
      // traduction commune qui en décide, pas ce module.
      let corps: unknown;
      try {
        corps = JSON.parse(requete.responseText) as unknown;
      } catch {
        corps = null;
      }

      conclus(traduitUneReponse<T>(requete.status, corps));
    });

    // `abort` couvre AUSSI la veille qui a coupé : une attente sans fin et une
    // coupure franche laissent la même chose à la personne — rien — et se
    // disent donc de la même façon.
    for (const fin of ['error', 'timeout', 'abort']) {
      requete.addEventListener(fin, () => conclus(sansReponse<T>()));
    }

    requete.open(demande.methode, chemin);

    // Le dépôt reçoit les octets en corps brut : c'est le contrat de la route,
    // et c'est ce qui évite toute bibliothèque multipart de part et d'autre.
    requete.setRequestHeader('content-type', 'application/octet-stream');
    rearme();
    requete.send(demande.octets);
  });
}
